"use client";

import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@universe/ui/components/dropdown-menu";
import { cn } from "@universe/ui/lib/utils";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";

import AccountTypeIcon from "@/components/account-type-icon";
import { NoTranslate } from "@/components/no-translate";
import { useAccountTypeLabel } from "@/utils/formatAccountType";
import { CustomerAccountUser } from "@generated/types";

/**
 * The "Switch account" section of a user dropdown: heading, multi-account
 * hint, and one row per customer account with the active row marked.
 *
 * Shared between BreadcrumbHeader (in-app) and AccountStateGate's blocking
 * page so the two menus can't drift — the CALLER owns what selecting a row
 * does (the header sequences switches through the dashboard; the gate
 * switches immediately since no app content is mounted).
 */
export function AccountSwitchList({
  accounts,
  activeAccountId,
  onSelect,
}: {
  accounts: CustomerAccountUser[];
  activeAccountId: string | null;
  onSelect: (account: CustomerAccountUser) => void;
}) {
  const tNav = useTranslations("nav");
  const tCommon = useTranslations("common");
  const accountTypeLabel = useAccountTypeLabel();

  return (
    <>
      <DropdownMenuLabel className="px-2 py-1.5 text-sm font-semibold">
        {tNav("switchAccount")}
      </DropdownMenuLabel>
      {accounts.length > 1 && (
        <div className="px-2 pb-2 text-xs font-medium text-muted-foreground">
          {tNav("selectAccountDescription")}
        </div>
      )}
      <DropdownMenuSeparator />
      {accounts.map((account) => {
        const isActive = account.id === activeAccountId;
        return (
          <DropdownMenuLabel
            className="p-0 font-normal my-1"
            key={account.id}
            onClick={(e) => {
              e.preventDefault();
              if (isActive) {
                return;
              }
              onSelect(account);
            }}
          >
            <DropdownMenuItem
              className={cn(
                "hover:bg-brand-primary/5 py-3",
                isActive
                  ? "bg-brand-primary/10 cursor-default"
                  : "hover:cursor-pointer"
              )}
            >
              <div className="flex items-center gap-2 px-1 text-left w-full">
                <div className="grid flex-1 text-left leading-tight gap-1">
                  <span className="truncate font-semibold text-gray-900">
                    <NoTranslate>{account.customerAccount?.name}</NoTranslate>
                  </span>
                  <span className="truncate text-muted-foreground font-normal text-sm">
                    <AccountTypeIcon
                      type={account.customerAccount?.accountType || ""}
                      label={
                        accountTypeLabel(
                          account.customerAccount?.accountType
                        ) ?? undefined
                      }
                      showLabel={true}
                      size={14}
                      iconClassName="text-gray-700"
                      labelClassName="text-gray-700"
                    />
                  </span>
                </div>
                {isActive && (
                  <div className="flex items-center gap-1 text-xs text-primary font-medium">
                    <Check className="size-3.5" />
                    <span>{tCommon("active")}</span>
                  </div>
                )}
              </div>
            </DropdownMenuItem>
          </DropdownMenuLabel>
        );
      })}
    </>
  );
}
